<script setup>
import { ref, onMounted, computed } from "vue";
import { useTherapistStore } from "@/stores/therapist";
import { useLocationStore } from "@/stores/location";
import { DateTime } from "luxon";
import axios from "@/lib/axios";
import Swal from "sweetalert2";
//import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import NestedMultiSelect from "./components/NestedMultiSelect.vue";

const isLoading = ref(true);
const therapistStore = useTherapistStore();
const locationStore = useLocationStore();
const showActionDialog = ref(false);
const showImportDialog = ref(false);
const actions = ref([]);
const processing = ref(false);
const pollInterval = ref(null);
const timezoneOptions = [ "UTC", "Location" ];
const showResultsModal = ref(false);
const actionResults = ref([]);
const locationLookup = ref({});
const locations = ref({});
const displayParent = ref({});

const newAction = ref({
    therapist: null,
    locations: [],
    type: "absent",
    dateFrom: DateTime.now().toISODate(),
    dateTo: DateTime.now().plus({ days: 7 }).toISODate(),
    timeFrom: "09:00",
    timeTo: "12:00",
    timezone: "Location",
    repeat: {
        daily: false,
        weekly: false,
        monthly: false
    },
    overwrite: false
});

const types = [ "absent", "hart_hours", "delete" ];
const eventLabel = {
    absent: "Absent",
    hart_hours: "Hart Hours",
    delete: "DELETE"
};

function generateActions() {
    const startDate = DateTime.fromISO(newAction.value.dateFrom);
    const endDate = DateTime.fromISO(newAction.value.dateTo);
    const generatedActions = [];

    let currentDate = startDate;
    while (currentDate <= endDate) {
        for (const locationId of newAction.value.locations) {
            const location = locationLookup.value[locationId];
            if (!location) continue;
            const date = currentDate.toISODate();

            // Convert times based on timezone selection
            const zone = newAction.value.timezone === "Location" ? location.timezone ?? "UTC" : "UTC";
            const tzFrom = DateTime.fromISO(`${date}T${newAction.value.timeFrom}`, { zone: zone })
                .setZone("UTC")
                .toFormat("yyyy-MM-dd HH:mm:ss");
            const tzTo = DateTime.fromISO(`${date}T${newAction.value.timeTo}`, { zone: zone })
                .setZone("UTC")
                .toFormat("yyyy-MM-dd HH:mm:ss");

            generatedActions.push({
                id: [ ...Array(64) ].map(() => Math.random().toString(36)[2]).join(""),
                location: location,
                therapistName: displayParent.value[location.therapist_id],
                type: newAction.value.type,
                timeFrom: tzFrom,
                timeTo: tzTo,
                overwrite: newAction.value.overwrite
            });
        }

        if (newAction.value.repeat.daily) {
            currentDate = currentDate.plus({ days: 1 });
        } else if (newAction.value.repeat.weekly) {
            currentDate = currentDate.plus({ weeks: 1 });
        } else if (newAction.value.repeat.monthly) {
            currentDate = currentDate.plus({ months: 1 });
        } else {
            break;
        }
    }

    actions.value = [ ...actions.value, ...generatedActions ];
    showActionDialog.value = false;
}

async function executeActions() {
    if (!actions.value.length) return;

    isLoading.value = true;
    try {
        processing.value = true;
        const response = await axios.post("/server/api/events/mass_actions", {
            actions: actions.value.map((action) => {
                return {
                    therapist_id: action.location.therapist_id,
                    location_id: action.location.ROWID,
                    type: action.type,
                    time_start: action.timeFrom,
                    time_end: action.timeTo,
                    overwrite: action.overwrite
                };
            })
        });

        // If response includes a job_id, start polling
        if (response.data.status === "success") {
            startPolling(response.data.job_id);
        } else if (response.data.status === "processing") {
            // Another job is already running
            isLoading.value = false;
            await Swal.fire({
                icon: "warning",
                title: "System Busy",
                text: "Another mass action is currently being processed. Please try again in a few minutes."
            });
        }
    } catch (error) {
        console.error("Failed to execute actions:", new Error(error));
        await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to execute actions. Please try again."
        });
        isLoading.value = false;
    } finally {
        processing.value = false;
    }
}

function startPolling() {
    pollInterval.value = setInterval(async () => {
        try {
            const response = await axios.get("/server/api/events/mass_actions_status");

            if (response.data.status === "processing") return;

            stopPolling();

            // Use location info for results
            if (response.data.message.results) {
                actionResults.value = response.data.message.results.map((result) => {
                    // Find original action to get location info
                    const originalAction = actions.value.find((a) => a.id === result.id);
                    return {
                        ...result,
                        location: originalAction?.location
                    };
                });
                showResultsModal.value = true;
            }

            actions.value = [];

            const successCount = actionResults.value.filter((r) => r.status === "success").length;
            const failCount = actionResults.value.filter((r) => r.status === "error").length;

            await Swal.fire({
                icon: failCount ? "warning" : "success",
                title: "Mass Actions Completed",
                html: `
                    Successfully processed: ${successCount}<br>
                    Failed: ${failCount}
                `
            });
        } catch (error) {
            console.error("Polling error:", new Error(error));
            stopPolling();
        }
    }, 2500);
}

function stopPolling() {
    if (pollInterval.value) {
        clearInterval(pollInterval.value);
        pollInterval.value = null;
    }
    isLoading.value = false;
    processing.value = false;
}

function deleteAction(actionId) {
    actions.value = actions.value.filter((a) => a.id !== actionId);
}

function clearActions() {
    actions.value = [];
}

// Add new refs
const csvHeaderAlias = {
    "location": "location_crm_id",
    "location_id": "location_crm_id",
    "loc_id": "location_crm_id",
    "loc": "location_crm_id",
    "crm_location_id": "location_crm_id",
    "crmlocation": "location_crm_id",
    "crmlocationid": "location_crm_id",
    "start": "startTime",
    "start_time": "startTime",
    "starttime": "startTime",
    "start time": "startTime",
    "time start": "startTime",
    "end": "endTime",
    "end_time": "endTime",
    "endtime": "endTime",
    "end time": "endTime",
    "time end": "endTime",
    "finish": "endTime",
    "type": "type",
    "event": "type",
    "event_type": "type",
    "event type": "type",
    "action": "type",
    "action_type": "type",
    "slot": "type",
    "slot_type": "type",
};
const csvData = ref(null);
const csvHeaders = ref([]);
const csvMapping = ref({
    location_crm_id: "",
    startTime: "",
    endTime: "",
    type: "",
});
const useFileType = ref(false);
const selectedStaticType = ref("absent");
const dropzoneFile = ref(null);
const isImportValid = computed(() => {
    return csvMapping.value.location_crm_id &&
            csvMapping.value.startTime &&
            csvMapping.value.endTime &&
            (!useFileType.value && selectedStaticType.value ||
            useFileType.value && csvMapping.value.type);
});

// Add drag and drop handling
function handleDragOver(e) {
    e.preventDefault();
}

function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
}

function handleFileInput(e) {
    const file = e.target.files[0];
    handleFile(file);
}

async function handleFile(file) {
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
        const proceed = await Swal.fire({
            icon: "warning",
            title: "Non-CSV File",
            text: "The file does not appear to be a CSV. Do you want to try processing it anyway?",
            showCancelButton: true,
            confirmButtonText: "Yes, proceed",
            cancelButtonText: "No, cancel"
        });

        if (!proceed.isConfirmed) return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
        const text = e.target.result;
        // Split by newlines and filter out empty lines
        const lines = text.split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        if (lines.length < 2) {
            await Swal.fire({
                icon: "error",
                title: "Empty File",
                text: "File appears to be empty"
            });
            return;
        }

        // Parse headers
        csvHeaders.value = lines[0].split(",").map((h) => h.trim());

        // Parse data rows and filter out rows with all empty values
        const rows = lines.slice(1)
            .map((line) => {
                const values = line.split(",").map((v) => v.trim());
                return csvHeaders.value.reduce((obj, header, i) => {
                    obj[header] = values[i] || undefined;
                    return obj;
                }, {});
            })
            .filter((row) => Object.values(row).some((v) => v !== undefined && v !== ""));

        csvData.value = rows;

        // Auto-map headers using aliases
        csvHeaders.value.forEach((header) => {
            const lowerHeader = header.toLowerCase();
            if (csvHeaderAlias[lowerHeader]) {
                csvMapping.value[csvHeaderAlias[lowerHeader]] = header;
            }
        });
    };
    reader.readAsText(file);
}

function clearFile() {
    csvData.value = null;
    csvHeaders.value = [];
    csvMapping.value = {
        location_crm_id: "",
        startTime: "",
        endTime: "",
        type: ""
    };
    useFileType.value = false;
    selectedStaticType.value = "absent";
}

function getPreviewRow() {
    if (!csvData.value?.length) return null;
    return csvData.value[0];
}

function createDateTime(isoOrDate, timeStr = null) {
    const zone = "UTC";

    if (timeStr) {
        return DateTime.fromISO(`${isoOrDate}T${timeStr}`, { zone: zone }).toFormat("yyyy-MM-dd HH:mm:ss");
    }

    // Handle SQL datetime format (YYYY-MM-DD HH:mm:ss)
    if (typeof isoOrDate === "string" && isoOrDate.includes(" ")) {
        return DateTime.fromSQL(isoOrDate, { zone: zone }).toFormat("yyyy-MM-dd HH:mm:ss");
    }

    // Handle Date objects
    if (isoOrDate instanceof Date) {
        return DateTime.fromJSDate(isoOrDate, { zone: zone }).toFormat("yyyy-MM-dd HH:mm:ss");
    }

    // Handle DateTime objects
    if (isoOrDate instanceof DateTime) {
        return isoOrDate.setZone(zone).toFormat("yyyy-MM-dd HH:mm:ss");
    }

    // Handle ISO strings
    return DateTime.fromISO(isoOrDate, { zone: zone }).toFormat("yyyy-MM-dd HH:mm:ss");
}

async function handleImport() {
    if (!csvData.value?.length) return;

    const generatedActions = [];
    const errors = [];

    for (const row of csvData.value) {
        try {
            // Find location by id
            const location = locationLookup.value[row[csvMapping.value.location_crm_id]];
            if (!location) {
                errors.push(`Location not found for location_crm_id: ${row[csvMapping.value.location_crm_id]}`);
                continue;
            }

            const type = useFileType.value ? row[csvMapping.value.type] : selectedStaticType.value;
            if (!types.includes(type)) {
                errors.push(`Invalid type "${type}" for location_crm_id: ${row[csvMapping.value.location_crm_id]}`);
                continue;
            }

            generatedActions.push({
                id: Math.random().toString(36).substr(2, 9),
                location: location,
                therapistName: displayParent.value[location.therapist_id],
                type: type,
                timeFrom: createDateTime(row[csvMapping.value.startTime]),
                timeTo: createDateTime(row[csvMapping.value.endTime]),
                overwrite: false
            });
        } catch (err) {
            errors.push(`Error processing row: ${JSON.stringify(row)}`);
        }
    }

    if (errors.length) {
        await Swal.fire({
            icon: "warning",
            title: "Import completed with errors",
            html: errors.join("<br>")
        });
    }

    actions.value = [ ...actions.value, ...generatedActions ];
    showImportDialog.value = false;
}

function toggleAll() {
    const overwrite = actions.value.every((a) => a.overwrite);
    actions.value.forEach((a) => {
        a.overwrite = !overwrite;
    });
}

onMounted(async () => {
    if (!therapistStore.therapists.length) {
        await therapistStore.fetchTherapists();
    }

    displayParent.value = therapistStore.therapists.reduce((acc, t) => {
        acc[t.ROWID] = `${t?.first_name ?? ""} ${t?.last_name ?? ""}`;
        return acc;
    }, {});

    await Promise.all([
        ...therapistStore.therapists.length ? [] : [ therapistStore.fetchTherapists() ],
        locationStore.fetchAllLocations().then((res) => {
            locations.value = res.reduce((acc, item) => {
                if (!acc[item.therapist_id]) acc[item.therapist_id] = [];
                locationLookup.value[item.ROWID] = item;
                acc[item.therapist_id].push(item);
                return acc;
            }, {});
        })
    ]);

    isLoading.value = false;
});
</script>

<template>
    <!-- Actions List -->
    <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
            <div class="flex flex-col">
                <h2 class="text-xl font-semibold">Mass Actions</h2>
                <h6 class="text-sm text-gray-600">NOTE: All shown time here is in UTC</h6>
            </div>

            <div class="space-x-2">
                <button
                    @click="clearActions"
                    class="px-4 py-2 text-red-600 border border-red-600 rounded hover:bg-red-50"
                    :disabled="!actions.length"
                >
                    Clear All
                </button>
                <button
                    @click="showActionDialog = true;"
                    class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    New Action
                </button>
                <button
                    @click="executeActions"
                    class="px-4 py-2 bg-green-500 text-white rounded disabled:cursor-not-allowed disabled:bg-green-300 hover:bg-green-600"
                    :disabled="actions.length<=0 || processing"
                >
                    {{ processing ? 'Processing...' : 'Execute All' }}
                </button>
            </div>
        </div>

        <!-- Actions Table -->
        <div class="overflow-x-auto">
            <table v-if="actions.length" class="w-full">
                <thead>
                    <tr class="border-b">
                        <th class="py-2 text-left hover:text-blue-500 cursor-pointer" @click="toggleAll">Overwrite</th>
                        <th class="px-4 py-2 text-left">Therapist</th>
                        <th class="px-4 py-2 text-left">Location</th>
                        <th class="px-4 py-2 text-left">Type</th>
                        <th class="px-4 py-2 text-left">From</th>
                        <th class="px-4 py-2 text-left">To</th>
                        <th class="px-4 py-2 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="action in actions" :key="action.id" class="border-b">
                        <td class="flex w-full justify-start px-4 py-2">
                            <label class="inline-flex items-center cursor-pointer">
                                <input type="checkbox" v-model="action.overwrite" class="sr-only peer">
                                <div
                                    class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer
                                    dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                                    peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                                    after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all
                                    dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"
                                />
                            </label>
                        </td>
                        <td class="px-4 py-2">{{ action.therapistName + " - " + action.location.name }}</td>
                        <td class="px-4 py-2">
                            <span :class="{
                                'text-red-600': action.type === 'delete',
                                'text-yellow-600': action.type === 'absent',
                                'text-green-600': action.type === 'hart_hours'
                            }">{{ action.type }}</span>
                        </td>
                        <td class="px-4 py-2">{{ action.timeFrom }}</td>
                        <td class="px-4 py-2">{{ action.timeTo }}</td>
                        <td class="px-4 py-2 text-right">
                            <button
                                @click="deleteAction(action.id)"
                                class="text-red-600 hover:text-red-800"
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="text-center py-8 text-gray-500">
                No actions created yet. Click "New Action" to start.
            </div>
        </div>
    </div>

    <!-- New Action Dialog -->
    <dialog
        v-if="showActionDialog"
        class="fixed inset-0 flex items-center justify-center w-full h-full bg-gray-900 bg-opacity-75"
    >
        <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div class="flex w-full justify-between">
                <h3 class="text-lg font-semibold mb-4">Create New Action</h3>
                <button
                    @click="showImportDialog = true; showActionDialog = false;"
                    class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Import
                </button>
            </div>

            <!-- Form -->
            <div class="space-y-4">
                <!-- Location Selection -->
                <div>
                    <label class="block text-sm font-medium">Select Locations</label>
                    <NestedMultiSelect
                        v-model="newAction.locations"
                        :values="locations"
                        :display-parent="displayParent"
                        child-display-key="name"
                        :export-child-array="true"
                    />
                    <!-- <VueMultiselect
                        v-model="newAction.locations"
                        :options="locationStore.locations"
                        :multiple="true"
                        track-by="ROWID"
                        value-prop="ROWID"
                        :custom-label="(location) => location.name"
                        :close-on-select="false"
                        :clear-on-select="false"
                        :preserve-search="true"
                        placeholder="Select locations"
                    /> -->
                </div>

                <!-- Timezone selection -->
                <div>
                    <label class="block text-sm font-medium">Timezone</label>
                    <select
                        v-model="newAction.timezone"
                        class="w-full border rounded p-2"
                    >
                        <option
                            v-for="tz in timezoneOptions"
                            :key="tz"
                            :value="tz"
                        >
                            {{ tz }}
                        </option>
                    </select>
                </div>

                <!-- Type Selection -->
                <div>
                    <label class="block text-sm font-medium">Event Type</label>
                    <select
                        v-model="newAction.type"
                        class="w-full border rounded p-2"
                        :class="{'text-red-600': newAction.type === 'delete'}"
                    >
                        <option
                            v-for="type in types"
                            :key="type"
                            :value="type"
                            :class="{'text-red-600': type === 'delete'}"
                        >
                            {{ eventLabel[type] || type }}
                        </option>
                    </select>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium">From Date</label>
                        <input
                            type="date"
                            v-model="newAction.dateFrom"
                            class="w-full border rounded p-2"
                        >
                    </div>
                    <div>
                        <label class="block text-sm font-medium">To Date</label>
                        <input
                            type="date"
                            v-model="newAction.dateTo"
                            class="w-full border rounded p-2"
                        >
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium">Start Time</label>
                        <input
                            type="time"
                            v-model="newAction.timeFrom"
                            class="w-full border rounded p-2"
                        >
                    </div>
                    <div>
                        <label class="block text-sm font-medium">End Time</label>
                        <input
                            type="time"
                            v-model="newAction.timeTo"
                            class="w-full border rounded p-2"
                        >
                    </div>
                </div>

                <!-- Repeat Options -->
                <div>
                    <label class="block text-sm font-medium">Repeat Options</label>
                    <div class="space-x-4">
                        <label class="inline-flex items-center">
                            <input
                                type="checkbox"
                                v-model="newAction.repeat.daily"
                                class="mr-2"
                            >
                            Daily
                        </label>
                        <label class="inline-flex items-center">
                            <input
                                type="checkbox"
                                v-model="newAction.repeat.weekly"
                                class="mr-2"
                            >
                            Weekly
                        </label>
                        <label class="inline-flex items-center">
                            <input
                                type="checkbox"
                                v-model="newAction.repeat.monthly"
                                class="mr-2"
                            >
                            Monthly
                        </label>
                    </div>
                </div>

                <!-- Overwrite -->
                <div>
                    <label class="inline-flex items-center cursor-pointer">
                        <input type="checkbox" v-model="newAction.overwrite" class="sr-only peer">
                        <div
                            class="relative w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700
                            peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                            peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                            after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5
                            after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"
                        ></div>
                        <span class="ms-3 text-sm font-medium text-gray-900">Overwrite</span>
                    </label>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end space-x-2 mt-6">
                <button
                    @click="showActionDialog = false"
                    class="px-4 py-2 border rounded hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    @click="generateActions()"
                    class="px-4 py-2 bg-blue-500 disabled:cursor-not-allowed disabled:bg-gray-300 text-white rounded hover:bg-blue-600"
                    :disabled="!newAction.locations.length"
                >
                    Generate Actions
                </button>
            </div>
        </div>
    </dialog>

    <!-- File Import Dialog -->
    <dialog v-if="showImportDialog" class="fixed inset-0 flex items-center justify-center w-full h-full bg-gray-900 bg-opacity-75">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl h-[80vh] flex flex-col">
            <!-- Sticky Header -->
            <div class="sticky top-0 bg-white z-10 p-4 border-b">
                <div class="flex justify-between items-center">
                    <h3 class="text-lg font-semibold">Import CSV</h3>
                    <button @click="showImportDialog = false" class="text-gray-500 hover:text-gray-700">×</button>
                </div>
            </div>

            <!-- Scrollable Content -->
            <div class="flex-1 overflow-y-auto p-6">
                <!-- Drop Zone -->
                <div @dragover="handleDragOver"
                    @drop="handleDrop"
                    @click="dropzoneFile.click()"
                    class="flex flex-col items-center justify-center w-full h-64 border-2 border-blue-300 border-dashed
                    rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                >
                    <div class="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg class="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                            <path
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071
                                5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                        </svg>
                        <p class="mb-2 text-sm text-gray-500"><span class="font-semibold">Click to upload</span> or drag and drop</p>
                        <p class="text-xs text-gray-500">CSV</p>
                    </div>
                    <input ref="dropzoneFile" id="dropzone-file" type="file" class="hidden" accept=".csv" @change="handleFileInput" />
                </div>

                <!-- Clear File Button -->
                <div v-if="csvData" class="mt-4 flex justify-end">
                    <button @click="clearFile"
                            class="text-red-600 hover:text-red-700 text-sm">
                        Clear File
                    </button>
                </div>

                <!-- Mapping Section with improved styling -->
                <div v-if="csvData" class="space-y-4 mt-4">
                    <div v-for="field in ['location_crm_id', 'startTime', 'endTime']" :key="field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ field.charAt(0).toUpperCase() + field.slice(1) }} Field
                        </label>
                        <select
                            v-model="csvMapping[field]"
                            class="w-full border border-blue-300 rounded-md p-2 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        >
                            <option value="">Select field</option>
                            <option v-for="header in csvHeaders" :key="header" :value="header">
                                {{ header }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1">Type</label>
                        <div class="flex w-full justify-between">
                            <label class="inline-flex items-center">
                                <input type="radio" v-model="useFileType" :value="false" class="mr-2">
                                Static Type:
                                <select v-model="selectedStaticType" class="ml-2 border rounded p-1" :disabled="useFileType">
                                    <option v-for="type in types" :key="type" :value="type">
                                        {{ eventLabel[type] || type }}
                                    </option>
                                </select>
                            </label>
                            <label class="inline-flex items-center">
                                <input type="radio" v-model="useFileType" :value="true" class="mr-2">
                                From File:
                                <select v-model="csvMapping.type" class="ml-2 border rounded p-1" :disabled="!useFileType">
                                    <option value="">Select field</option>
                                    <option v-for="header in csvHeaders" :key="header" :value="header">
                                        {{ header }}
                                    </option>
                                </select>
                            </label>
                        </div>
                    </div>

                    <div>
                        <label class="inline-flex items-center cursor-pointer">
                        <input type="checkbox" v-model="newAction.overwrite" class="sr-only peer">
                        <div class="relative w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full
                        rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px]
                        after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all
                        dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                        <span class="ms-3 text-sm font-medium text-gray-900">Overwrite</span>
                    </label>
                    </div>

                    <!-- Preview -->
                    <div v-if="getPreviewRow()" class="mt-4">
                        <h4 class="font-medium mb-2">First row file preview:</h4>
                        <div class="bg-gray-50 p-3 rounded">
                            <pre class="text-sm">{{ JSON.stringify(getPreviewRow(), null, 2) }}</pre>
                        </div>
                    </div>
                </div>

                <!-- Sticky Footer -->
                <div class="bg-white p-4 border-t">
                    <div class="flex justify-end space-x-2">
                        <button @click="showImportDialog = false"
                                class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                            Close
                        </button>
                        <button @click="handleImport"
                                class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                :disabled="!isImportValid">
                            Import
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </dialog>

    <!-- Loader -->
    <dialog
        v-show="isLoading"
        class="fixed inset-0 z-50 flex flex-col items-center justify-center w-full h-full bg-gray-900 bg-opacity-75"
    >
        <svg
            aria-hidden="true"
            class="w-24 h-24 animate-spin"
            viewBox="0 0 100 101"
            xmlns="http://www.w3.org/2000/svg"
        >
        <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142
            0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186
            50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="slategray"
        />
        <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192
            80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345
            1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191
            9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175
            28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="blue"
        />
        </svg>
    </dialog>

    <!-- Results Modal -->
    <dialog
        v-if="showResultsModal"
        class="fixed inset-0 flex items-center justify-center w-full h-full bg-gray-900 bg-opacity-75"
    >
        <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] flex flex-col">
            <!-- Header -->
            <div class="sticky top-0 bg-white z-10 p-4 border-b">
                <div class="flex justify-between items-center">
                    <h3 class="text-lg font-semibold">Mass Actions Results</h3>
                    <button @click="showResultsModal = false" class="text-gray-500 hover:text-gray-700">×</button>
                </div>
            </div>

            <!-- Content -->
            <div class="overflow-y-auto p-4">
                <table class="w-full">
                    <thead>
                        <tr class="border-b">
                            <th class="px-4 py-2 text-left">Status</th>
                            <th class="px-4 py-2 text-left">Therapist / Location</th>
                            <th class="px-4 py-2 text-left">Type</th>
                            <th class="px-4 py-2 text-left">Time Range</th>
                            <th class="px-4 py-2 text-left">Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="result in actionResults"
                            :key="result.id"
                            class="border-b"
                            :class="{
                                'bg-red-50': result.status === 'error',
                                'bg-green-50': result.status === 'success'
                            }">
                            <td class="px-4 py-2">
                                <span :class="{
                                    'text-red-600': result.status === 'error',
                                    'text-green-600': result.status === 'success'
                                }">
                                    {{ result.status }}
                                </span>
                            </td>
                            <td class="px-4 py-2">
                                {{ displayParent[locationLookup[result.location_id].therapist_id] }}
                                -
                                {{ locationLookup[result.location_id].name }}
                            </td>
                            <td class="px-4 py-2">
                                <span :class="{
                                    'text-red-600': result.type === 'delete',
                                    'text-yellow-600': result.type === 'absent',
                                    'text-green-600': result.type === 'hart_hours'
                                }">{{ result.type }}</span>
                            </td>
                            <td class="px-4 py-2">
                                {{ result.time_start }}<br>
                                {{ result.time_end }}
                            </td>
                            <td class="px-4 py-2 text-red-600">{{ result.message }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Footer -->
            <div class="sticky bottom-0 bg-white p-4 border-t">
                <div class="flex justify-end">
                    <button
                        @click="showResultsModal = false"
                        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </dialog>
</template>

<style scoped>
.dialog-content {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.dialog-content::-webkit-scrollbar {
    width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
    background: transparent;
}

.dialog-content::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 3px;
}

select, input[type="radio"] {
    cursor: pointer;
}

select:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
}
</style>
